<template>
    <DialogLayout :class-name="'change-owner-dialog'" @close="handleClose">
        <template #title>{{ t('candidate.changeResponsiblePerson') }}</template>
        <template #default>
            <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'left'" :label-width="'100px'"
                class="change-owner-form">
                <el-form-item :label="t('candidate.responsiblePerson')" prop="ownerId" required>
                    <el-select v-model="formData.ownerId" :placeholder="t('common.pleaseSelect')" filterable clearable>
                        <template #empty>
                            <EmptyState type="empty" :title="t('common.noData')" />
                        </template>
                        <el-option v-for="option in ownerOptions" :key="option.id" :label="option.name"
                            :value="option.id" />
                    </el-select>
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" :loading="submitting" @click="handleConfirm">
                {{ t('common.save') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import EmptyState from '@/components/basic-components/empty-state/EmptyState.vue'
import type { ChangeOwnerDialogProps, OwnerOption } from './change-owner-dialog-types'
import { updateCandidateOwnerApi, getCandidateAccountApi } from '@/api/candidate'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const props = defineProps<ChangeOwnerDialogProps>()

const emit = defineEmits<{
    (e: 'confirm'): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const submitting = ref(false)

// 负责人选项列表
const ownerOptions = ref<OwnerOption[]>([])

// 表单数据
const formData = reactive<{
    ownerId?: number
}>({
    ownerId: undefined
})

// 表单验证规则
const rules: FormRules = {
    ownerId: [
        { required: true, message: t('candidate.pleaseSelectResponsiblePerson'), trigger: 'change' }
    ]
}

// 获取负责人选项
const fetchOwnerOptions = async () => {
    try {
        const response = await getCandidateAccountApi({ roleCodesExclude: ['ROLE_BUSINESS_OPERATION'] })
        if (response && Array.isArray(response)) {
            ownerOptions.value = response.map((item: Record<string, unknown>) => ({
                id: (item.id as number) || 0,
                name: (item.name as string) || ''
            }))
        } else if (response && response.list && Array.isArray(response.list)) {
            ownerOptions.value = response.list.map((item: Record<string, unknown>) => ({
                id: (item.id as number) || 0,
                name: (item.name as string) || ''
            }))
        }
    } catch (error) {
        console.error('获取负责人选项失败:', error)
    }
}

// 初始化表单数据（回显）
onMounted(() => {
    fetchOwnerOptions()
    if (props.ownerId) {
        formData.ownerId = props.ownerId
    }
})

// 确认保存
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        // 先验证表单，如果验证失败会抛出错误，不会继续执行
        await formRef.value.validate()
    } catch (error: unknown) {
        // 表单验证失败，不显示额外提示（Element Plus 会自动显示），直接返回，不提交接口
        if (error && typeof error === 'object' && 'fields' in error) {
            return
        }
        // 其他验证错误也直接返回
        return
    }

    // 验证通过后，才执行 API 调用
    try {
        submitting.value = true

        // 调用 API 更新负责人
        await updateCandidateOwnerApi({
            interviewId: props.interviewId,
            ownerId: formData.ownerId!
        })

        ElMessage.success(t('common.saveSuccess'))
        submitting.value = false
        // emit('confirm') 会自动关闭对话框，Promise 会 resolve
        emit('confirm')
    } catch (error: unknown) {
        submitting.value = false
        
        // API 调用失败
        const errorMessage = error && typeof error === 'object' && 'message' in error 
            ? String(error.message) 
            : t('common.saveFailed')
        console.error('保存失败:', error)
        ElMessage.error(errorMessage)
    }
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.change-owner-form {

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;

        &::before {
            display: none;
        }
    }

    :deep(.el-select) {
        width: 100%;
    }
}

:deep(.change-owner-dialog) {
    width: 512px;

    
}
</style>

<style lang="scss">
.change-owner-dialog {
    width: 512px !important;
    .dialog-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            padding: 20px 30px;
    
            .el-button {
                min-width: 80px;
            }
        }
}
</style>

